import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link as RouterLink, useParams, useHistory } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import axios from "axios";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import {
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
  Typography,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CloseIcon from "@material-ui/icons/Close";
import Footer from "../components/Footer";
import { ReactComponent as LumioLogo } from "../assets/img/lumio-drop.svg";
import { ReactComponent as LumioLogoMobile } from "../assets/img/lumio-drop-mobile.svg";
import { ReactComponent as InfoDraw } from "../assets/img/info-draw.svg";
import { ReactComponent as InfoError } from "../assets/img/info-error.svg";

function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + " " + units[u];
}

const useStyles = makeStyles((theme) => ({
  completeWrapper: {
    height: "auto",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  completeContainer: {
    paddingTop: 16,
    paddingBottom: 16,
    minHeight: "calc(100vh - 60px)",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
  },
  completeTitle: {
    marginBottom: 32,
    fontWeight: 600,
  },
  completeSubtitle2: {
    marginBottom: 8,
    lineHeight: "1.625em",
    color: theme.custom.colors.fontDark,
  },
  completeSubtitle1: {
    marginBottom: 0,
    lineHeight: "1.625em",
  },
  goToHomeButtonText: {
    textDecoration: "none",
  },
  goToHomeButton: {
    margin: "16px 0",
  },
  faq: {
    marginTop: 64,
    display: "flex",
    flexDirection: "column",
  },
  faqItem: {
    marginBottom: 6,
  },
  loader: {
    width: "100%",
    textAlign: "center",
  },
  textDruid: {
    fontWeight: 600,
    margin: "20px 0",
  },
  urlToCopy: {
    maxWidth: "100%",
    marginTop: 6,
    marginBottom: 32,
    border: "1px solid",
    borderColor: theme.custom.colors.borderLight,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "fit-content",
    [theme.breakpoints.up("sm")]: {
      minWidth: 328,
      width: "fit-content",
    },
  },
  urlToCopyText: {
    color: theme.custom.colors.fontLight,
    padding: 8,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  snackbarStyle: {
    backgroundColor: "red",
  },
  lumioLogo: {
    display: "none",
    marginTop: 32,
    marginBottom: 32,
    marginLeft: -60,
    [theme.breakpoints.up("lg")]: {
      display: "block",
    },
  },
  lumioLogoMobile: {
    display: "block",
    marginTop: 32,
    marginBottom: 20,
    marginLeft: -60,
    [theme.breakpoints.up("lg")]: {
      display: "none",
    },
  },
  descriptionWrapper: {
    position: "relative",
    zIndex: 9000,
  },
  infoDraw: {
    right: 0,
    width: 420,
    height: 420,
    bottom: -360,
    position: "absolute",
    zIndex: 1000,
    [theme.breakpoints.down("sm")]: {
      width: 260,
      height: 260,
      bottom: -240,
    },
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  infoError: {
    right: 0,
    width: 420,
    height: 420,
    bottom: -200,
    position: "absolute",
    zIndex: 1000,
    [theme.breakpoints.down("sm")]: {
      width: 320,
      height: 320,
      bottom: -260,
    },
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
}));

const MAX_RETRIES = 5;
const POLLING_INTERVAL = 3000;

const Info = () => {
  const classes = useStyles();
  const history = useHistory();
  const { druid } = useParams();
  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const retryCountRef = useRef(0);
  const timeoutRef = useRef(null);

  const handleClickSnackbar = () => {
    setCopySnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setCopySnackbar(false);
  };

 const fetchData = useCallback(async () => {
   try {
     const result = await axios.get("/drop/drop/info", { params: { druid } });
     if (result.status === 200) {
       setInfo(result.data);
       setIsLoading(false);
        retryCountRef.current = 0;
     } else if (!result || result.status === 404) {
       if (retryCountRef.current < MAX_RETRIES) {
         retryCountRef.current += 1;
         timeoutRef.current = setTimeout(fetchData, POLLING_INTERVAL);
       } else {
         history.push(`/landing/${404}`);
       }
     } else {
       throw new Error("Unexpected status");
     }
   } catch (error) {
     if (retryCountRef.current < MAX_RETRIES) {
       retryCountRef.current += 1;
       timeoutRef.current = setTimeout(fetchData, POLLING_INTERVAL);
     } else {
       history.push(`/landing/error-druid-${druid}`);
     }
   }
 }, [druid, history]);

 useEffect(() => {
   fetchData();
   return () => clearTimeout(timeoutRef.current);
 }, [fetchData]);

  const customRender = () => {
    if (info.count_file === 0) {
      return (
        <>
          <div className={classes.descriptionWrapper}>
            <Typography
              variant="subtitle2"
              className={classes.completeSubtitle2}
            >
              Oops, we failed to process your files!
            </Typography>

            <Typography
              variant="subtitle1"
              className={classes.completeSubtitle1}
            >
              Well, this is unexpected, please try to upload your files again.
              <br />
              if this problem persists show us this information.
            </Typography>

            <InfoError className={classes.infoError} />
          </div>

          <div>
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.goToHomeButton}
              component={RouterLink}
              to="/drop"
            >
              Retry Drop
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={classes.descriptionWrapper}>
          <Typography variant="subtitle2" className={classes.completeSubtitle2}>
            {info.count_file > 1
              ? info.count_file + " Files "
              : info.count_file + " File "}
            ({humanFileSize(info.count_size, true)}) are uploaded!
          </Typography>

          <Typography variant="subtitle1" className={classes.completeSubtitle1}>
            Please inform your Content Manager with the drop ID below so
            <br />
            they can access your uploaded files
          </Typography>

          <InfoDraw className={classes.infoDraw} />
        </div>

        <Typography variant="h4" className={classes.textDruid}>
          {druid}
        </Typography>

        <Typography variant="subtitle1" className={classes.completeSubtitle1}>
          You can go back to this page using the url below
        </Typography>

        <div className={classes.urlToCopy}>
          <Typography className={classes.urlToCopyText} variant="body2">
            {window.location.href}
          </Typography>
          <CopyToClipboard
            text={window.location.href}
            onCopy={handleClickSnackbar}
          >
            <Button
              style={{ minWidth: 100 }}
              size="small"
              startIcon={<FileCopyIcon fontSize="small" />}
            >
              COPY URL
            </Button>
          </CopyToClipboard>
        </div>

        <div>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={classes.goToHomeButton}
            component={RouterLink}
            to="/drop"
          >
            Drop More
          </Button>
        </div>
      </>
    );
  };

  // return customRender();

  return (
    <>
      <div className={classes.completeWrapper}>
        <Container maxWidth="md" className={classes.completeContainer}>
          {isLoading && (
            <div className={classes.loader}>
              <CircularProgress />
            </div>
          )}
          {!isLoading && (
            <>
              <LumioLogo className={classes.lumioLogo} />
              <LumioLogoMobile className={classes.lumioLogoMobile} />
              {customRender()}
            </>
          )}

          <Snackbar
            open={copySnackbar}
            autoHideDuration={5000}
            onClose={handleCloseSnackbar}
            message="URL copied!"
            action={
              <React.Fragment>
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={handleCloseSnackbar}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </React.Fragment>
            }
          />
        </Container>

        <Footer />
      </div>
    </>
  );
};

export default Info;
